import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { axiosInstance } from "@/App";
import { getCameraDisplayFit } from "../utils/cameraCapture";

const IVS_PLAYER_VERSION = "1.53.0";
const IVS_PLAYER_SCRIPT = `https://player.live-video.net/${IVS_PLAYER_VERSION}/amazon-ivs-player.min.js`;

export const VIEWER_BUFFER_STATUS_POLL_MS = 2_500;
export const VIEWER_BUFFER_DEGRADED_POLL_MS = 15_000;
export const VIEWER_BUFFER_DISABLED_POLL_MS = 60_000;
export const VIEWER_BUFFER_START_TIMEOUT_MS = 12_000;
export const VIEWER_BUFFER_VIDEO_STALL_TIMEOUT_MS = 8_000;
export const VIEWER_BUFFER_FRAME_CHECK_MS = 250;
export const VIEWER_AUDIO_ROUTE_RECOVERY_MS = 4_000;
export const VIEWER_AUDIO_ROUTE_MUTE_HOLD_MS = 1_800;

let sdkPromise;

export const resolveViewerPlaybackFit = (sourceOrientation, viewportOrientation) => (
  getCameraDisplayFit(
    viewportOrientation,
    sourceOrientation === "landscape"
      ? { width: 2, height: 1 }
      : { width: 1, height: 2 },
  )
);

const loadIvsSdk = () => {
  if (window.IVSPlayer) return Promise.resolve(window.IVSPlayer);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${IVS_PLAYER_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.IVSPlayer), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = IVS_PLAYER_SCRIPT;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = () => resolve(window.IVSPlayer);
    script.onerror = () => reject(new Error("Unable to load the AWS IVS Player SDK"));
    document.head.appendChild(script);
  });

  return sdkPromise;
};

/**
 * Stable, one-way viewer buffer session.
 *
 * The direct LiveKit feed remains active until IVS proves it decoded a real
 * video frame. The session then commits to IVS and lets the IVS player handle
 * ordinary buffering and adaptive quality internally. A terminal IVS error
 * falls back to LiveKit once and the same IVS URL is not retried until the
 * viewer reloads or the event receives a different playback URL.
 */
const IvsPlaybackLayer = ({
  eventId,
  orientation = "portrait",
  onPlaybackChange,
  onFallbackRequired,
  playbackState = null,
  realtimeConnected = false,
  statusEndpoint,
  audioRouteTransitionKey = 0,
  externalAudioMuted = false,
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const failedUrlRef = useRef(null);
  const playbackUrlRef = useRef(null);
  const statusPollingDoneRef = useRef(false);
  const audioRouteTransitionUntilRef = useRef(0);
  const audioRouteMutedUntilRef = useRef(0);
  const externalAudioMutedRef = useRef(externalAudioMuted);
  const previousExternalAudioMutedRef = useRef(externalAudioMuted);
  const previousAudioRouteTransitionKeyRef = useRef(audioRouteTransitionKey);
  const audioRouteResumeTimersRef = useRef([]);
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [phase, setPhase] = useState("checking");
  const [visible, setVisible] = useState(false);
  const [sourceOrientation, setSourceOrientation] = useState(orientation);
  const [viewportOrientation, setViewportOrientation] = useState(() => (
    typeof window !== "undefined" && window.innerWidth > window.innerHeight
      ? "landscape"
      : "portrait"
  ));

  const applyPlaybackState = useCallback((status = {}) => {
    const nextUrl = status.active && status.playback_url
      ? String(status.playback_url)
      : null;
    if (!status.enabled) {
      playbackUrlRef.current = null;
      failedUrlRef.current = null;
      setPlaybackUrl(null);
      setVisible(false);
      setPhase("disabled");
      onPlaybackChange?.(false);
    } else if (nextUrl && nextUrl !== playbackUrlRef.current) {
      statusPollingDoneRef.current = true;
      playbackUrlRef.current = nextUrl;
      failedUrlRef.current = null;
      setPlaybackUrl(nextUrl);
      setPhase("connecting");
    } else if (!playbackUrlRef.current) {
      setPhase(status.state || "waiting");
    }
  }, [onPlaybackChange]);

  useEffect(() => {
    let active = true;

    failedUrlRef.current = null;
    playbackUrlRef.current = null;
    statusPollingDoneRef.current = false;
    setPlaybackUrl(null);
    setVisible(false);
    setPhase("checking");
    onPlaybackChange?.(false);

    const bootstrap = async () => {
      try {
        const response = await axiosInstance.get(statusEndpoint || `/livekit/ivs/playback/${eventId}`);
        if (!active) return;
        applyPlaybackState(response.data || {});
      } catch (error) {
        if (active && !playbackUrlRef.current) setPhase("waiting");
      }
    };

    bootstrap();
    return () => {
      active = false;
    };
  }, [applyPlaybackState, eventId, onPlaybackChange, statusEndpoint]);

  useEffect(() => {
    if (playbackState) applyPlaybackState(playbackState);
  }, [applyPlaybackState, playbackState]);

  // Starting or stopping a WebRTC microphone changes the mobile device's
  // audio route. Safari and Chromium-based in-app browsers can briefly pause
  // or emit an IVS error during that handoff even though the stream is still
  // healthy. Keep the existing video element visible and resume the same IVS
  // player in place; never rebuild or swap the viewer's video for this event.
  useLayoutEffect(() => {
    if (previousAudioRouteTransitionKeyRef.current === audioRouteTransitionKey) {
      return undefined;
    }
    previousAudioRouteTransitionKeyRef.current = audioRouteTransitionKey;
    audioRouteTransitionUntilRef.current = Date.now() + VIEWER_AUDIO_ROUTE_RECOVERY_MS;
    audioRouteMutedUntilRef.current = Date.now() + VIEWER_AUDIO_ROUTE_MUTE_HOLD_MS;

    audioRouteResumeTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    audioRouteResumeTimersRef.current = [];
    const resumeExistingPlayer = () => {
      const player = playerRef.current;
      const video = videoRef.current;
      if (!player || !video) return;
      const keepAudioMuted = (
        externalAudioMutedRef.current
        || Date.now() < audioRouteMutedUntilRef.current
      );
      video.muted = keepAudioMuted;
      player.setVolume?.(keepAudioMuted ? 0 : 1);
      Promise.resolve(player.play?.()).catch(() => {});
    };
    resumeExistingPlayer();
    [200, 750, VIEWER_AUDIO_ROUTE_MUTE_HOLD_MS + 50].forEach((delay) => {
      audioRouteResumeTimersRef.current.push(window.setTimeout(resumeExistingPlayer, delay));
    });

    return () => {
      audioRouteResumeTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      audioRouteResumeTimersRef.current = [];
    };
  }, [audioRouteTransitionKey]);

  // The selected audience speaker hears the creator through the low-latency
  // LiveKit conversation bridge. Keep the IVS element as picture-only during
  // that session so mobile browsers never have to move the visible video
  // element between playback and two-way WebRTC audio sessions.
  useLayoutEffect(() => {
    if (previousExternalAudioMutedRef.current && !externalAudioMuted) {
      audioRouteMutedUntilRef.current = Math.max(
        audioRouteMutedUntilRef.current,
        Date.now() + VIEWER_AUDIO_ROUTE_MUTE_HOLD_MS,
      );
    }
    previousExternalAudioMutedRef.current = externalAudioMuted;
    externalAudioMutedRef.current = externalAudioMuted;
    const player = playerRef.current;
    const video = videoRef.current;
    if (!player || !video) return undefined;

    const applyAudioRoute = () => {
      const keepAudioMuted = (
        externalAudioMutedRef.current
        || Date.now() < audioRouteMutedUntilRef.current
      );
      video.muted = keepAudioMuted;
      player.setVolume?.(keepAudioMuted ? 0 : 1);
      Promise.resolve(player.play?.()).catch(() => {});
    };
    applyAudioRoute();
    let timer;
    if (!externalAudioMuted && Date.now() < audioRouteMutedUntilRef.current) {
      timer = window.setTimeout(
        applyAudioRoute,
        Math.max(audioRouteMutedUntilRef.current - Date.now(), 0) + 25,
      );
    }
    return () => window.clearTimeout(timer);
  }, [externalAudioMuted]);

  // REST status is a degraded recovery path only. Healthy sessions receive
  // relay state over the event's single realtime control connection.
  useEffect(() => {
    if (realtimeConnected || playbackUrlRef.current) return undefined;
    let active = true;
    let timer;
    const recover = async () => {
      try {
        const response = await axiosInstance.get(statusEndpoint || `/livekit/ivs/playback/${eventId}`);
        if (active) applyPlaybackState(response.data || {});
      } catch (_) {}
      if (active && !playbackUrlRef.current) {
        timer = window.setTimeout(recover, VIEWER_BUFFER_DEGRADED_POLL_MS);
      }
    };
    timer = window.setTimeout(recover, VIEWER_BUFFER_DEGRADED_POLL_MS);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [applyPlaybackState, eventId, playbackUrl, realtimeConnected, statusEndpoint]);

  useEffect(() => {
    const video = videoRef.current;
    if (!playbackUrl || !video || failedUrlRef.current === playbackUrl) return undefined;

    const updateViewportOrientation = () => {
      const bounds = video.parentElement?.getBoundingClientRect?.();
      const width = Number(bounds?.width || window.innerWidth || 0);
      const height = Number(bounds?.height || window.innerHeight || 0);
      if (width > 0 && height > 0) {
        setViewportOrientation(width > height ? "landscape" : "portrait");
      }
    };
    updateViewportOrientation();
    const viewportObserver = typeof ResizeObserver === "function"
      ? new ResizeObserver(updateViewportOrientation)
      : null;
    viewportObserver?.observe(video.parentElement || video);

    let disposed = false;
    let player;
    let activated = false;
    let frameTimer;
    let startDeadlineTimer;
    let unmuteTimer;
    let buffering = false;
    let lastDecodedFrameAt = Date.now();
    let lastDecodedFrameCount = null;
    let mediaTimeAtLastDecodedFrame = 0;
    let terminalRecoveryTimer;
    let terminalSignalPending = false;
    let removeGestureListener = () => {};
    let removeVideoListeners = () => {};

    const syncSourceOrientation = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setSourceOrientation(video.videoHeight >= video.videoWidth ? "portrait" : "landscape");
      }
    };

    const stopFrameChecks = () => {
      window.clearInterval(frameTimer);
      window.clearTimeout(startDeadlineTimer);
      frameTimer = undefined;
      startDeadlineTimer = undefined;
    };

    const decodedFrameCount = () => {
      try {
        const total = video.getVideoPlaybackQuality?.().totalVideoFrames;
        if (Number.isFinite(total)) return Number(total);
      } catch (_) {}
      const webkitTotal = Number(video.webkitDecodedFrameCount);
      return Number.isFinite(webkitTotal) ? webkitTotal : null;
    };

    const failToLiveKit = () => {
      if (disposed || failedUrlRef.current === playbackUrl) return;
      failedUrlRef.current = playbackUrl;
      stopFrameChecks();
      window.clearTimeout(unmuteTimer);
      window.clearTimeout(terminalRecoveryTimer);
      video.muted = true;
      setVisible(false);
      setPhase("failed");
      onPlaybackChange?.(false);
      onFallbackRequired?.();
      try { player?.delete?.(); } catch (_) {}
    };

    const recoverAudioRouteWithoutVideoHandoff = () => {
      if (disposed) return;
      const remaining = audioRouteTransitionUntilRef.current - Date.now();
      if (remaining <= 0) {
        failToLiveKit();
        return;
      }

      terminalSignalPending = true;
      setPhase("recovering-audio-route");
      const keepAudioMuted = (
        externalAudioMutedRef.current
        || Date.now() < audioRouteMutedUntilRef.current
      );
      video.muted = keepAudioMuted;
      player?.setVolume?.(keepAudioMuted ? 0 : 1);
      Promise.resolve(player?.play?.()).catch(() => {});
      window.clearTimeout(terminalRecoveryTimer);
      terminalRecoveryTimer = window.setTimeout(() => {
        if (!disposed && terminalSignalPending) failToLiveKit();
      }, Math.max(remaining, 250));
    };

    const handleTerminalPlaybackSignal = () => {
      if (Date.now() < audioRouteTransitionUntilRef.current) {
        recoverAudioRouteWithoutVideoHandoff();
        return;
      }
      failToLiveKit();
    };

    const updateDecodedFrameHealth = () => {
      const nextCount = decodedFrameCount();
      if (nextCount !== null && (
        lastDecodedFrameCount === null || nextCount > lastDecodedFrameCount
      )) {
        lastDecodedFrameCount = nextCount;
        lastDecodedFrameAt = Date.now();
        mediaTimeAtLastDecodedFrame = Number(video.currentTime || 0);
      }
    };

    const checkPlaybackHealth = () => {
      if (disposed) return;
      if (!activated) {
        revealDecodedFrame();
        return;
      }

      updateDecodedFrameHealth();
      const visibleDocument = document.visibilityState !== "hidden";
      const mediaAdvancedWithoutVideo = (
        Number(video.currentTime || 0) - mediaTimeAtLastDecodedFrame >= 0.75
      );
      const hasFrameCounter = lastDecodedFrameCount !== null;
      const videoSurfaceMissing = (
        video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
        || video.videoWidth <= 0
        || video.videoHeight <= 0
      );
      if (
        visibleDocument
        && !buffering
        && Date.now() - lastDecodedFrameAt >= VIEWER_BUFFER_VIDEO_STALL_TIMEOUT_MS
        && (videoSurfaceMissing || (hasFrameCounter && mediaAdvancedWithoutVideo))
      ) {
        console.warn("[AWS IVS] audio advanced without decoded video; returning viewer to LiveKit");
        failToLiveKit();
      }
    };

    const revealDecodedFrame = () => {
      if (
        disposed
        || activated
        || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
        || video.videoWidth <= 0
        || video.videoHeight <= 0
      ) {
        return;
      }

      activated = true;
      syncSourceOrientation();
      window.clearTimeout(startDeadlineTimer);
      startDeadlineTimer = undefined;
      lastDecodedFrameCount = decodedFrameCount();
      lastDecodedFrameAt = Date.now();
      mediaTimeAtLastDecodedFrame = Number(video.currentTime || 0);
      setVisible(true);
      setPhase("playing");
      onPlaybackChange?.(true);

      // Let React disconnect the direct LiveKit room before IVS audio is
      // unmuted, preventing even a brief duplicate-audio overlap.
      unmuteTimer = window.setTimeout(() => {
        if (disposed || !activated) return;
        const keepAudioMuted = (
          externalAudioMutedRef.current
          || Date.now() < audioRouteMutedUntilRef.current
        );
        video.muted = keepAudioMuted;
        player?.setVolume?.(keepAudioMuted ? 0 : 1);
        Promise.resolve(player?.play?.()).catch(() => {});
      }, 0);
    };

    const beginFrameChecks = () => {
      if (disposed) return;
      if (!activated) setPhase("priming");
      window.clearInterval(frameTimer);
      frameTimer = window.setInterval(checkPlaybackHealth, VIEWER_BUFFER_FRAME_CHECK_MS);
      revealDecodedFrame();
    };

    const resumeAfterGesture = () => {
      if (disposed || !player) return;
      if (activated) {
        video.muted = (
          externalAudioMutedRef.current
          || Date.now() < audioRouteMutedUntilRef.current
        );
      }
      Promise.resolve(player.play()).catch(() => {});
    };

    const start = async () => {
      try {
        const IVSPlayer = await loadIvsSdk();
        if (disposed || !IVSPlayer?.isPlayerSupported) {
          failToLiveKit();
          return;
        }

        player = IVSPlayer.create({
          serviceWorker: {
            url: "/amazon-ivs-service-worker-loader.js",
          },
        });
        playerRef.current = player;
        player.attachHTMLVideoElement(video);
        video.muted = true;
        player.setVolume?.(0);

        player.addEventListener(IVSPlayer.PlayerState.PLAYING, () => {
          terminalSignalPending = false;
          window.clearTimeout(terminalRecoveryTimer);
          buffering = false;
          beginFrameChecks();
        });
        // Once IVS is visible, BUFFERING is owned by the IVS player. Switching
        // sources here was the cause of viewer jerking in the previous build.
        player.addEventListener(IVSPlayer.PlayerState.BUFFERING, () => {
          buffering = true;
          if (!activated) setPhase("buffering");
        });
        player.addEventListener(IVSPlayer.PlayerState.ENDED, handleTerminalPlaybackSignal);
        player.addEventListener(IVSPlayer.PlayerEventType.ERROR, handleTerminalPlaybackSignal);

        const handleVideoReady = () => {
          syncSourceOrientation();
          revealDecodedFrame();
        };
        const videoReadyEvents = ["loadedmetadata", "loadeddata", "canplay", "playing", "timeupdate", "resize"];
        videoReadyEvents.forEach((eventName) => video.addEventListener(eventName, handleVideoReady));
        removeVideoListeners = () => {
          videoReadyEvents.forEach((eventName) => video.removeEventListener(eventName, handleVideoReady));
        };

        document.addEventListener("pointerdown", resumeAfterGesture, { passive: true });
        removeGestureListener = () => document.removeEventListener("pointerdown", resumeAfterGesture);

        startDeadlineTimer = window.setTimeout(
          failToLiveKit,
          VIEWER_BUFFER_START_TIMEOUT_MS,
        );
        player.load(playbackUrl);
        Promise.resolve(player.play()).catch(() => {
          // Muted playback should normally start automatically. If a browser
          // still blocks it, the next user gesture retries without changing
          // media sources.
          setPhase("awaiting-gesture");
        });
      } catch (error) {
        if (!disposed) {
          console.warn("[AWS IVS] clean viewer buffer connection failed:", error);
          failToLiveKit();
        }
      }
    };

    start();
    return () => {
      disposed = true;
      stopFrameChecks();
      window.clearTimeout(unmuteTimer);
      window.clearTimeout(terminalRecoveryTimer);
      removeGestureListener();
      removeVideoListeners();
      viewportObserver?.disconnect();
      video.muted = true;
      if (activated) onPlaybackChange?.(false);
      if (playerRef.current === player) playerRef.current = null;
      try { player?.delete?.(); } catch (_) {}
    };
  }, [playbackUrl, onFallbackRequired, onPlaybackChange]);

  const objectFit = resolveViewerPlaybackFit(sourceOrientation, viewportOrientation);

  return (
    <div
      className={`pointer-events-none absolute inset-0 z-[5] bg-transparent transition-opacity duration-200 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
      aria-hidden={!visible}
      data-testid="ivs-playback-layer"
      data-ivs-playing={visible ? "true" : "false"}
      data-buffer-phase={phase}
      data-external-audio-muted={externalAudioMuted ? "true" : "false"}
      data-creator-orientation={sourceOrientation}
      data-fit={objectFit}
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        crossOrigin="anonymous"
        className="h-full w-full bg-transparent"
        style={{
          objectFit,
          objectPosition: "center",
        }}
      />
    </div>
  );
};

export default IvsPlaybackLayer;
